import { useEffect, useState } from 'react';
import { notifications } from '@mantine/notifications';
import * as Cesium from 'cesium';
import { BehaviorParameter } from '../../../../../types/behaviors';
import { AreasData } from '../../../../../types/geometries';
import { createAreas } from '../functions/createAreas';
import { createPolygons } from '../functions/createPolygons';

interface IBaseLayer {
	viewer: Cesium.Viewer;
	side: 0 | 1;
	areasData: AreasData | null | undefined;
	voronoisDataSource: Cesium.GeoJsonDataSource | null | undefined;
	elementOnWhichInputDepends?: BehaviorParameter;
	setIsDataLoading: (isLoading: boolean) => void;
	setIsBaseLayerLoaded: (isLoaded: boolean) => void;
}

const BaseLayer: React.FC<IBaseLayer> = ({
	viewer,
	side,
	areasData,
	voronoisDataSource,
	setIsDataLoading,
	setIsBaseLayerLoaded,
}) => {
	const [areasDataSource, setAreasDataSource] = useState<Cesium.GeoJsonDataSource | null>(null);

	useEffect(() => {
		let cancelled = false;

		const loadAreas = async () => {
			if (!areasData) return;

			setIsDataLoading(true);

			if (areasData.features.length === 0) {
				notifications.show({
					title: 'Error',
					message: 'No areas found for given geography and resolution',
					color: 'red',
					autoClose: 5000,
				});
			}

			const filtered = {
				...areasData,
				features: areasData.features.filter((feature: any) =>
					feature.properties.owner.toLowerCase().includes(side === 1 ? 'red' : 'blue')
				),
			};

			const dataSource = await Cesium.GeoJsonDataSource.load(filtered);
			if (!cancelled) setAreasDataSource(dataSource);

			setIsDataLoading(false);
		};

		if (viewer && viewer.dataSources && areasData) {
			loadAreas();
		}

		return () => {
			cancelled = true;
		};

		// We dont need to update this effect when setIsDataLoading changes as it's always the same function
	}, [viewer, areasData, side]);

	useEffect(() => {
		if (areasDataSource && voronoisDataSource) {
			createPolygons(viewer, voronoisDataSource);
			createAreas(viewer, areasDataSource);
			setIsBaseLayerLoaded(true);
		}
	}, [viewer, voronoisDataSource, areasDataSource]);

	return null;
};

export default BaseLayer;
